import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Leaf,
  GraduationCap,
  Landmark,
  Scale,
} from "lucide-react";
import SouvenirPdfDownloadButton from "./SouvenirPdfDownloadButton";

const FORUMS = [
  {
    icon: Landmark,
    title: "Socio Civic & Political Forum",
    description:
      "We encourage young people to register as voters and take up civic issues affecting our city and community. This forum networks with more than 15 NGOs across Mumbai and India.",
    highlight: {
      icon: Scale,
      title: "Legal aid for the marginalized",
      text: (
        <>
          In collaboration with{" "}
          <span className="font-medium text-ink-900">
            DSLA (District Legal Services Authority)
          </span>
          , retired judges provide free legal advice to marginalized members of our
          society.
        </>
      ),
    },
  },
  {
    icon: Leaf,
    title: "Environment Forum",
    description:
      "We promote green and solar energy, composting, and plastic-waste collection in parishes. The Sabha stood at the forefront of protests against the destruction of Aarey Colony.",
  },
  {
    icon: GraduationCap,
    title: "Youth & Training Forum",
    description:
      "Our academy trains youth for civil services and government placement examinations. In the last two years, trained members have joined the DRI and various government departments—with the IAS as our aim.",
  },
];

function ForumCard({ icon: Icon, title, description, highlight, index }) {
  const HighlightIcon = highlight?.icon;

  return (
    <li
      className="rise-in h-full"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="h-full flex flex-col bg-white border border-line rounded-2xl p-6 sm:p-7 shadow-card hover:shadow-card-hover hover:border-burgundy-600/20 transition-all duration-300">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-burgundy-100 text-burgundy-600 mb-5 shrink-0">
          <Icon size={20} strokeWidth={2} aria-hidden />
        </span>
        <h3 className="font-heading font-semibold text-lg text-ink-900 leading-snug">
          {title}
        </h3>
        <p className="mt-3 text-sm text-ink-700 leading-relaxed flex-1">
          {description}
        </p>
        {highlight ? (
          <div
            className="mt-5 pt-5 border-t border-line flex gap-3 sm:gap-3.5"
            data-testid="hero-dsla"
          >
            <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gold-50 border border-gold-300/60 text-gold-600">
              <HighlightIcon size={16} strokeWidth={2} aria-hidden />
            </span>
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-gold-600">
                {highlight.title}
              </p>
              <p className="mt-1.5 text-xs sm:text-sm text-ink-700 leading-relaxed">
                {highlight.text}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </li>
  );
}

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden bg-ivory-50"
      data-testid="hero-section"
      id="home"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-5">
              Archdiocese of Bombay · Laity Organisation
            </p>
            <h1 className="font-heading font-semibold text-ink-900 text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05] tracking-tight">
              Serving Mumbai&apos;s Catholic community
              <span className="block text-burgundy-600">with faith, action and purpose.</span>
            </h1>

            <div className="gold-rule my-7" aria-hidden="true" />

            <p className="text-ink-700 text-base sm:text-lg max-w-2xl leading-relaxed">
              The Bombay Catholic Sabha is a recognized laity organisation of the
              Archdiocese of Mumbai, with more than{" "}
              <span className="font-semibold text-ink-900">67,689 life members</span>.
              We work across every deanery in the Archdiocese—and this Sabha Day 2026
              Souvenir honours the supporters who stand with us.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#sponsors"
                className="inline-flex items-center gap-2 rounded-lg bg-burgundy-600 hover:bg-burgundy-700 text-white font-medium px-5 py-3 shadow-card transition-colors"
                data-testid="hero-cta-primary"
              >
                Meet the supporters
                <ArrowRight size={16} />
              </a>
              <Link
                to="/sponsors"
                className="inline-flex items-center gap-2 rounded-lg border border-burgundy-600 text-burgundy-600 hover:bg-burgundy-100 font-medium px-5 py-3 transition-colors"
                data-testid="hero-cta-secondary"
              >
                <BookOpen size={16} />
                Browse full directory
              </Link>
              <SouvenirPdfDownloadButton />
            </div>

            <dl className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl">
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Life members
                </dt>
                <dd className="mt-1 font-heading text-2xl text-ink-900">67,689+</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Deaneries
                </dt>
                <dd className="mt-1 font-heading text-2xl text-ink-900">9</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Active forums
                </dt>
                <dd className="mt-1 font-heading text-2xl text-ink-900">3</dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[28px] overflow-hidden border border-line shadow-card-hover">
              <img
                src="https://images.unsplash.com/photo-1663321231228-2625873fbdcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjaHVyY2glMjBpbnRlcmlvciUyMHdhcm0lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc3ODgzMDg4fDA&ixlib=rb-4.1.0&q=85"
                alt="Warm church interior with rows of wooden chairs."
                className="w-full h-[420px] sm:h-[480px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-burgundy-700/25 via-transparent to-transparent" />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white border border-line rounded-xl p-4 shadow-card w-56">
              <p className="text-xs uppercase tracking-wider text-gold-500 font-semibold">
                Souvenir
              </p>
              <p className="mt-1 font-heading text-ink-900 leading-snug">
                Sabha Day 2026 — printed keepsake
              </p>
            </div>
          </div>
        </div>

        {/* Our forums */}
        <div
          className="mt-20 sm:mt-24 pt-16 sm:pt-20 border-t border-line"
          data-testid="hero-forums"
        >
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gold-500 mb-4">
              What we do
            </p>
            <h2 className="font-heading font-semibold text-ink-900 text-2xl sm:text-3xl leading-tight tracking-tight">
              Three forums, one mission across the Archdiocese
            </h2>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Based in Mumbai, we operate in every deanery of the Archdiocese of
              Bombay through dedicated forums that serve civic life, the environment,
              and the next generation of leaders.
            </p>
          </div>

          <ul className="mt-10 sm:mt-12 grid gap-6 md:grid-cols-3 items-stretch">
            {FORUMS.map((forum, index) => (
              <ForumCard key={forum.title} {...forum} index={index} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
